<template>
  <v-dialog
    v-model="internalShow"
    max-width="800"
    scrollable
    :fullscreen="isMobile"
    :z-index="3100"
  >
    <v-card class="policy-modal">
      <v-card-title class="policy-modal__header">
        <div class="policy-modal__header-content">
          <v-icon start icon="mdi-file-document" color="primary" size="24" />
          <span class="policy-modal__header-title">Terms of Use</span>
        </div>
        <v-btn
          icon="mdi-close"
          variant="text"
          size="small"
          @click="close"
          aria-label="Close terms of use"
        />
      </v-card-title>

      <v-divider />

      <v-card-text class="policy-modal__content">
        <div class="policy-modal__last-updated">
          Effective Date: September 1, 2025
        </div>

        <p class="policy-modal__intro">
          These Terms of Use ("Terms") govern your use of the websites,
          services, platforms, applications, and music-related programs
          (collectively, the "Services") operated by ePongo, Inc. ("ePongo,"
          "we," "our," or "us").
        </p>
        <p class="policy-modal__intro">
          By accessing or using the Services, you agree to be bound by these
          Terms. If you do not agree, do not use the Services.
        </p>

        <section class="policy-modal__section">
          <h3>1. Eligibility</h3>
          <p>
            You must be at least 18 years of age (or the age of majority in your
            jurisdiction) to use the Services, unless you have the consent of a
            parent or legal guardian. By using the Services, you represent that
            you have the legal capacity to enter into binding agreements.
          </p>
        </section>

        <section class="policy-modal__section">
          <h3>2. Account Registration</h3>
          <p>
            Certain Services may require you to register for an account. You
            agree to provide accurate, current, and complete information during
            the registration process and to keep your account credentials
            secure. You are responsible for all activity that occurs under your
            account.
          </p>
        </section>

        <section class="policy-modal__section">
          <h3>3. Artist Programs and Agreements</h3>
          <p>
            If you are an artist enrolling in an ePongo program (Tier 1, Tier 2,
            Tier 3, or any successor program), you must also agree to the
            applicable Artist Agreement(s). In the event of a conflict between
            these Terms and an Artist Agreement, the Artist Agreement shall
            control with respect to program participation.
          </p>
        </section>

        <section class="policy-modal__section">
          <h3>4. Verified Listener Program</h3>
          <p>
            If you elect to participate in the Verified Listener Program, you
            agree to abide by any additional program terms published by ePongo
            from time to time. Verified Listener status may be revoked at
            ePongo's discretion for abuse, fraud, or violation of these Terms.
          </p>
        </section>

        <section class="policy-modal__section">
          <h3>5. Contests and Promotions</h3>
          <p>
            From time to time, ePongo may conduct contests or promotions,
            including the "World Series of Music." By participating, you agree
            to the official contest rules. In case of conflict between these
            Terms and the contest rules, the contest rules shall govern
            participation.
          </p>
        </section>

        <section class="policy-modal__section">
          <h3>6. License Grant</h3>
          <p>
            Subject to compliance with these Terms, ePongo grants you a limited,
            non-exclusive, non-transferable, revocable license to access and use
            the Services for lawful personal or professional purposes. You may
            not reverse engineer, reproduce, distribute, or exploit the Services
            except as expressly permitted.
          </p>
        </section>

        <section class="policy-modal__section">
          <h3>7. User Content</h3>
          <p>
            By submitting any music, lyrics, images, text, or other content
            ("User Content") to the Services, you represent and warrant that you
            own or control the necessary rights. You grant ePongo a
            non-exclusive, royalty-free, worldwide license to use, reproduce,
            distribute, perform, and display such User Content in connection
            with the Services.
          </p>
        </section>

        <section class="policy-modal__section">
          <h3>8. Prohibited Conduct</h3>
          <p>You agree not to:</p>
          <ul>
            <li>Violate any applicable law or regulation</li>
            <li>Infringe intellectual property or privacy rights</li>
            <li>Upload harmful code or engage in malicious activity</li>
            <li>Misrepresent your identity or impersonate others</li>
            <li>Interfere with the operation or security of the Services</li>
          </ul>
        </section>

        <section class="policy-modal__section">
          <h3>9. Payments and Fees</h3>
          <p>
            Some Services may require payment of fees. You agree to pay all
            applicable fees in accordance with the pricing and billing terms
            published by ePongo. All fees are non-refundable unless otherwise
            required by law.
          </p>
        </section>

        <section class="policy-modal__section">
          <h3>10. Intellectual Property</h3>
          <p>
            All content, branding, and technology comprising the Services are
            owned by ePongo or its licensors and are protected by copyright,
            trademark, and other intellectual property laws. Except as expressly
            permitted, you may not use ePongo's name, logo, or trademarks
            without prior written consent.
          </p>
        </section>

        <section class="policy-modal__section">
          <h3>11. Disclaimer of Warranties</h3>
          <p>
            The Services are provided "AS IS" and "AS AVAILABLE" without
            warranties of any kind, whether express or implied, including but
            not limited to implied warranties of merchantability, fitness for a
            particular purpose, and non-infringement. ePongo does not guarantee
            uninterrupted or error-free operation.
          </p>
        </section>

        <section class="policy-modal__section">
          <h3>12. Limitation of Liability</h3>
          <p>
            To the fullest extent permitted by law, ePongo shall not be liable
            for any indirect, incidental, consequential, special, or exemplary
            damages arising from your use of the Services. Our aggregate
            liability shall not exceed the amount paid by you (if any) to ePongo
            in the twelve (12) months preceding the claim.
          </p>
        </section>

        <section class="policy-modal__section">
          <h3>13. Indemnification</h3>
          <p>
            You agree to defend, indemnify, and hold harmless ePongo and its
            officers, directors, employees, and agents from and against all
            claims, liabilities, damages, losses, and expenses arising out of
            your use of the Services or your violation of these Terms.
          </p>
        </section>

        <section class="policy-modal__section">
          <h3>14. Termination</h3>
          <p>
            ePongo may suspend or terminate your access to the Services at any
            time for violation of these Terms or for business reasons. Upon
            termination, all licenses granted to you shall cease.
          </p>
        </section>

        <section class="policy-modal__section">
          <h3>15. Governing Law and Dispute Resolution</h3>
          <p>
            These Terms are governed by the laws of the State of Nevada, without
            regard to conflict of laws principles. Any disputes shall be
            resolved exclusively by binding arbitration in Las Vegas, Nevada.
          </p>
        </section>

        <section class="policy-modal__section">
          <h3>16. Modifications</h3>
          <p>
            ePongo reserves the right to modify these Terms at any time. Updated
            versions will be posted on the Services with a revised Effective
            Date. Continued use of the Services constitutes acceptance of the
            modified Terms.
          </p>
        </section>

        <section class="policy-modal__section">
          <h3>17. Contact Information</h3>
          <p>
            If you have questions about these Terms, please contact us at:
            <a href="mailto:legal@epongo.com" class="policy-modal__link"
              >legal@epongo.com</a
            >
          </p>
        </section>
      </v-card-text>

      <v-divider />

      <v-card-actions class="policy-modal__actions">
        <v-spacer />
        <v-btn color="primary" variant="elevated" @click="close">Close</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useDisplay } from "vuetify";

interface Props {
  modelValue: boolean;
}

interface Emits {
  (e: "update:modelValue", value: boolean): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const { mobile: isMobile } = useDisplay();

const internalShow = computed({
  get: () => props.modelValue,
  set: (value) => emit("update:modelValue", value),
});

function close() {
  internalShow.value = false;
}
</script>

<style scoped lang="scss">
.policy-modal {
  background: rgb(var(--v-theme-surface));
  border: 1px solid rgba(var(--v-theme-on-surface), 0.08);

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 20px 24px;
    padding-top: calc(20px + var(--safe-area-top));
    background: rgba(var(--v-theme-on-surface), 0.02);
  }

  &__header-content {
    display: flex;
    align-items: center;
    gap: 12px;
    flex: 1;
  }

  &__header-title {
    font-family: var(--font-display);
    font-size: 1.25rem;
    font-weight: 600;
    color: rgb(var(--v-theme-on-surface));
  }

  &__content {
    padding: 24px;
    line-height: 1.6;
  }

  &__last-updated {
    font-size: 0.875rem;
    color: rgba(var(--v-theme-on-surface), 0.6);
    margin-bottom: 24px;
    padding: 8px 12px;
    background: rgba(var(--v-theme-on-surface), 0.03);
    border-radius: 8px;
  }

  &__intro {
    color: rgba(var(--v-theme-on-surface), 0.7);
    margin-bottom: 16px;
  }

  &__section {
    margin-bottom: 32px;

    &:last-child {
      margin-bottom: 0;
    }

    h3 {
      font-family: var(--font-display);
      font-size: 1.125rem;
      font-weight: 600;
      color: rgb(var(--v-theme-on-surface));
      margin-bottom: 12px;
      padding-bottom: 8px;
      border-bottom: 2px solid #f59e0b;
    }

    h4 {
      font-size: 1rem;
      font-weight: 500;
      color: rgba(var(--v-theme-on-surface), 0.5);
      margin: 16px 0 8px;
    }

    p {
      color: rgba(var(--v-theme-on-surface), 0.7);
      margin-bottom: 12px;
    }

    ul {
      margin: 12px 0;
      padding-left: 20px;

      li {
        color: rgba(var(--v-theme-on-surface), 0.7);
        margin-bottom: 6px;
      }
    }
  }

  &__link {
    color: #f59e0b;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }

  &__actions {
    padding: 16px 24px;
    padding-bottom: calc(16px + var(--safe-area-bottom));
  }
}

// Light theme
.v-theme--light {
  .policy-modal {
    background: rgb(var(--v-theme-surface));
    border-color: rgba(var(--v-theme-on-surface), 0.08);

    &__header {
      background: rgba(var(--v-theme-on-surface), 0.02);
    }

    &__header-title {
      color: rgb(var(--v-theme-on-surface));
    }

    &__last-updated {
      color: rgba(var(--v-theme-on-surface), 0.4);
      background: rgba(var(--v-theme-on-surface), 0.03);
    }

    &__intro {
      color: #44403c;
    }

    &__section {
      h3 {
        color: rgb(var(--v-theme-on-surface));
        border-bottom-color: #d97706;
      }

      h4 {
        color: rgba(var(--v-theme-on-surface), 0.4);
      }

      p,
      li {
        color: #44403c;
      }
    }

    &__link {
      color: #d97706;
    }
  }
}

@media (max-width: 599px) {
  .policy-modal {
    &__header {
      padding: 16px 20px;
    }

    &__header-title {
      font-size: 1.125rem;
    }

    &__content {
      padding: 20px;
    }

    &__section {
      margin-bottom: 24px;

      h3 {
        font-size: 1rem;
      }

      h4 {
        font-size: 0.9375rem;
      }
    }
  }
}
</style>
